import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Calendar,
  ChevronDown,
  BarChart3,
  Settings,
  Pencil,
  ShoppingCart,
  X,
  List,
  Clock,
  MapPin,
  User,
  Tag as TagIcon,
  Download,
  LayoutGrid,
  CheckCircle2,
  XCircle,
  Flag,
} from "lucide-react";
import moment from "moment";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from "recharts";
import * as XLSX from "xlsx";
import { getAllCourts, getAllReservations, deleteReservation, getReservationReport, getActiveCategories, getAllCategories, createBulkReservations } from "../../api/courts";
import { getTournamentList } from "../../api/tournaments";
import { ITournamentItem } from "../../models/ITournament";
import { ICourt } from "../../models/ICourt";
import {
  ICourtReservation,
  ICreateReservationRequest,
  ReservationStatus,
  IReservationReport,
  IReservationCategory,
} from "../../models/ICourtReservation";
import ReservationDialog from "../../components/ReservationDialog";
import DeleteReservationDialog from "../../components/DeleteReservationDialog";
import RecurringTrainingDialog from "../../components/RecurringTrainingDialog";
import BulkReservationDialog from "../../components/BulkReservationDialog";
import CategoryDialog from "../../components/CategoryDialog";
import { setGeneralAlertMessage } from "../../store/slices/generalSlice";
import { useAppDispatch, useAppSelector } from "../../store/store";
import {
  hasAdminAccess,
  isHeadCoach,
  isCourtManager,
  isSuperAdmin,
  canCreateRegularReservations,
  canManageTrainingSchedules,
  canManageCourts,
} from "../../utils/authorization";
import CourtsManagement from "../../components/CourtsManagement";
import {
  Button,
  Card,
  Checkbox,
  DatePicker,
  Dialog,
  EmptyState,
  Field,
  IconButton,
  Loader,
  Menu,
  PageHeader,
  Segmented,
  Select,
  Spinner,
  StatCard,
  TableWrap,
  Tabs,
  Tag,
  Textarea,
  Toolbar,
} from "../../ui";
import type { StatCardProps, TabOption, TagTone } from "../../ui";
import "./styles.scss";

interface CartItem {
  id: string;
  courtId: string;
  courtName: string;
  startTime: string; // ISO string
  endTime: string; // ISO string
  displayStart: string; // "10:00 AM"
  displayEnd: string; // "11:00 AM"
}

type ReservationsTab = "calendar" | "report" | "categories" | "courts";

// A reservation status (the enum, or its number / name as a report key) → the status colour classes
// (crv-status--pending…, defined in styles.scss) and the kit Tag / StatCard tones.
type StatusKey = "pending" | "confirmed" | "cancelled" | "completed" | "unknown";
const STATUS_KEYS: Record<string, StatusKey> = {
  "1": "pending",
  "2": "confirmed",
  "3": "cancelled",
  "4": "completed",
  Pending: "pending",
  Confirmed: "confirmed",
  Cancelled: "cancelled",
  Completed: "completed",
};
const statusKeyOf = (status: ReservationStatus | string): StatusKey => STATUS_KEYS[String(status)] ?? "unknown";
const STATUS_TAG_TONES: Record<StatusKey, TagTone> = {
  pending: "warn",
  confirmed: "ok",
  cancelled: "danger",
  completed: "neutral",
  unknown: "neutral",
};
const STATUS_STAT_TONES: Record<StatusKey, NonNullable<StatCardProps["tone"]>> = {
  pending: "spark",
  confirmed: "ok",
  cancelled: "danger",
  completed: "neutral",
  unknown: "neutral",
};
const STATUS_ICONS: Record<StatusKey, React.ReactNode> = {
  pending: <Clock size={16} />,
  confirmed: <CheckCircle2 size={16} />,
  cancelled: <XCircle size={16} />,
  completed: <Flag size={16} />,
  unknown: <Clock size={16} />,
};
const LEGEND_STATUSES = [
  ReservationStatus.Pending,
  ReservationStatus.Confirmed,
  ReservationStatus.Cancelled,
  ReservationStatus.Completed,
];

// Category tags: Academy / Event / VIP keep their own colour, on brand tones
const CATEGORY_TONES: Record<string, TagTone> = {
  Academy: "solid-spark",
  Event: "solid-brand",
  VIP: "yellow",
};
const categoryToneOf = (categoryName: string | null | undefined): TagTone =>
  (categoryName && CATEGORY_TONES[categoryName]) || "neutral";

// Donut slices in a fixed order, brand tokens only (the first four pass the colour-vision checks side by side)
const CATEGORY_CHART_COLORS = [
  "#ff5536", // --spark
  "#2c42c3", // --brand
  "#cd7f32", // --medal-bronze
  "#687cf3", // --clash-blue-on-dark
  "#bf4028", // --spark-text
  "#fcc353", // --medal-gold
  "#5c5f66", // --muted
];

const CourtReservations = () => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.auth.currentUser);
  const isAdmin = hasAdminAccess(currentUser);
  const isHeadCoachUser = isHeadCoach(currentUser);
  const isCourtManagerUser = isCourtManager(currentUser);
  const isSuperAdminUser = isSuperAdmin(currentUser);
  const canCreateRegular = canCreateRegularReservations(currentUser);
  const canManageTraining = canManageTrainingSchedules(currentUser);
  const canViewReport = isSuperAdminUser || isCourtManagerUser || isHeadCoachUser;
  const canManageCategories = isSuperAdminUser || isCourtManagerUser;
  const canViewCourtsTab = canManageCourts(currentUser);
  const [courts, setCourts] = useState<ICourt[]>([]);
  const [categories, setCategories] = useState<IReservationCategory[]>([]);
  const [reservations, setReservations] = useState<ICourtReservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentDay, setCurrentDay] = useState(moment().startOf("day"));
  const [selectedReservation, setSelectedReservation] = useState<ICourtReservation | undefined>();
  const [isReservationDialogOpen, setIsReservationDialogOpen] = useState(false);
  const [initialReservationData, setInitialReservationData] = useState<{
    courtId?: string;
    date?: Date;
    startTime?: string;
  } | null>(null);
  const [isRecurringTrainingDialogOpen, setIsRecurringTrainingDialogOpen] = useState(false);
  const [isBulkReservationDialogOpen, setIsBulkReservationDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [reservationToDelete, setReservationToDelete] = useState<string>("");
  const reportReservationsRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<ReservationsTab>("calendar");
  const [reportFilterType, setReportFilterType] = useState<"thisMonth" | "lastMonth" | "custom">("thisMonth");
  const [reportStartDate, setReportStartDate] = useState("");
  const [reportEndDate, setReportEndDate] = useState("");
  const [reportData, setReportData] = useState<IReservationReport | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [selectedReportCategory, setSelectedReportCategory] = useState<string | null>(null);
  const [reportReservations, setReportReservations] = useState<ICourtReservation[]>([]);
  const [reportReservationsLoading, setReportReservationsLoading] = useState(false);
  const [calendarViewMode, setCalendarViewMode] = useState<"calendar" | "list">("calendar");
  const [listFilterCourt, setListFilterCourt] = useState<string>("");
  const [listFilterCategory, setListFilterCategory] = useState<string>("");
  // Categories management
  const [allCategories, setAllCategories] = useState<IReservationCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<IReservationCategory | undefined>();

  // Cart state for multi-slot selection
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [cartCategoryId, setCartCategoryId] = useState<string>("");
  const [cartTournamentId, setCartTournamentId] = useState<string>("");
  const [cartNeedBall, setCartNeedBall] = useState<Record<string, boolean>>({});
  const [cartNotes, setCartNotes] = useState("");
  const [cartLoading, setCartLoading] = useState(false);

  // Admin-only — optional tournament link for reservations (e.g. reserving courts for an event)
  const [tournaments, setTournaments] = useState<ITournamentItem[]>([]);

  // Tournaments whose date range covers the currently-viewed day. The cart
  // always creates reservations on `currentDay`, so the picker only offers
  // tournaments that are actually running then.
  const cartAvailableTournaments = useMemo(() => {
    return tournaments.filter((t) => {
      const s = moment(t.startDate).startOf("day");
      const e = moment(t.endDate).endOf("day");
      return currentDay.isSameOrAfter(s) && currentDay.isSameOrBefore(e);
    });
  }, [tournaments, currentDay]);

  // Find the "Event" category ID for bulk reservation default
  const eventCategory = useMemo(() => categories.find((c) => c.name === "Event"), [categories]);

  // Get sorted courts for display
  const sortedCourts = useMemo(() => {
    return [...courts].sort((a, b) => a.name.localeCompare(b.name));
  }, [courts]);

  // Generate time slots (7 AM to 1:00 AM, 30-minute intervals)
  const timeSlots = useMemo(() => {
    const slots: { hour: number; minute: number }[] = [];
    // From 7:00 AM to 11:30 PM (hour 7 to hour 23)
    for (let hour = 7; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        slots.push({ hour, minute });
      }
    }
    // From 12:00 AM to 1:00 AM (hour 0 to hour 1)
    for (let hour = 0; hour <= 1; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        slots.push({ hour, minute });
      }
    }
    return slots;
  }, []);

  const reservationsByCourt = useMemo(() => {
    const grouped: Record<string, ICourtReservation[]> = {};
    const dayKey = currentDay.format("YYYY-MM-DD");
    const nextDayKey = currentDay.clone().add(1, "day").format("YYYY-MM-DD");

    sortedCourts.forEach((court) => {
      grouped[court.id] = [];
    });

    reservations.forEach((reservation) => {
      const startDateKey =
        (reservation as any).__localDateKey ?? moment.utc(reservation.startTime).local().format("YYYY-MM-DD");
      const startLocal: moment.Moment =
        (reservation as any).__startLocal ?? moment.utc(reservation.startTime).local();

      const shouldInclude =
        startDateKey === dayKey || (startDateKey === nextDayKey && startLocal.hour() < 2);

      if (shouldInclude) {
        if (!grouped[reservation.courtId]) {
          grouped[reservation.courtId] = [];
        }
        grouped[reservation.courtId].push(reservation);
      }
    });

    return grouped;
  }, [sortedCourts, reservations, currentDay]);

  useEffect(() => {
    document.title = "Court Reservations";
    fetchData();
  }, []);

  useEffect(() => {
    fetchReservations();
  }, [currentDay]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [courtsRes, categoriesRes] = await Promise.all([getAllCourts(), getActiveCategories()]);
      setCourts(courtsRes.data);
      setCategories(categoriesRes.data);
    } catch (error: any) {
      dispatch(setGeneralAlertMessage({ message: error?.message || "Failed to load data", type: "error" }));
    } finally {
      setLoading(false);
    }

    // Admin-only: fetch tournaments to populate the optional "tournament" picker.
    // Restricted to Beach Volleyball — the only sport played on The Clash courts.
    // Best-effort — a failure here just means the picker is empty.
    if (isAdmin) {
      try {
        const res = await getTournamentList(1, 200, undefined, "startDate", "desc");
        const beachOnly = (res.data.items || []).filter(
          (t) => (t.type || "").toLowerCase() === "beach volleyball"
        );
        setTournaments(beachOnly);
      } catch {
        // swallow — feature is optional
      }
    }
  };

  const fetchReservations = async () => {
    setLoading(true);
    try {
      const dayEnd = currentDay.clone().add(1, "day").hour(2).minute(0).second(0).millisecond(0);
      const res = await getAllReservations({
        startDate: currentDay.toISOString(),
        endDate: dayEnd.toISOString(),
      });

      const normalized = (res.data || []).map((r: ICourtReservation) => {
        const startLocal = moment.utc(r.startTime).local();
        const endLocal = moment.utc(r.endTime).local();

        return {
          ...r,
          __startLocal: startLocal,
          __endLocal: endLocal,
          __localDateKey: startLocal.format("YYYY-MM-DD"),
        };
      });

      if (normalized.length > 0) {
        console.log("Normalized sample:", {
          startRaw: res.data[0].startTime,
          startLocal: normalized[0].__startLocal.format(),
        });
      }

      setReservations(normalized);
    } catch (error: any) {
      dispatch(setGeneralAlertMessage({ message: error?.message || "Failed to load reservations", type: "error" }));
    } finally {
      setLoading(false);
    }
  };

  const handlePreviousDay = () => {
    setCurrentDay(currentDay.clone().subtract(1, "day"));
  };

  const handleNextDay = () => {
    setCurrentDay(currentDay.clone().add(1, "day"));
  };

  const handleToday = () => {
    setCurrentDay(moment().startOf("day"));
  };

  const handleCreateReservation = () => {
    setSelectedReservation(undefined);
    setInitialReservationData(null);
    setIsReservationDialogOpen(true);
  };

  const handleSlotClick = (courtId: string, timeSlot: { hour: number; minute: number }) => {
    if (!canCreateRegular) {
      return;
    }

    const court = courts.find((c) => c.id === courtId);
    if (!court || !court.isActive) {
      return;
    }

    const slotStart = getSlotMoment(timeSlot);
    const slotEnd = slotStart.clone().add(30, "minutes");
    const itemId = `${courtId}_${slotStart.toISOString()}`;

    // Toggle: remove if already in cart
    const existingIndex = cartItems.findIndex((item) => item.id === itemId);
    if (existingIndex >= 0) {
      setCartItems((prev) => prev.filter((item) => item.id !== itemId));
      return;
    }

    // Add to cart
    const newItem: CartItem = {
      id: itemId,
      courtId,
      courtName: court.name,
      startTime: slotStart.toISOString(),
      endTime: slotEnd.toISOString(),
      displayStart: slotStart.format("hh:mm A"),
      displayEnd: slotEnd.format("hh:mm A"),
    };

    setCartItems((prev) => [...prev, newItem]);
  };

  const handleCreateRecurringTraining = () => {
    setIsRecurringTrainingDialogOpen(true);
  };

  const handleCreateBulkReservations = () => {
    setIsBulkReservationDialogOpen(true);
  };

  const handleEditReservation = (reservation: ICourtReservation) => {
    if (reservation.status === ReservationStatus.Pending || isAdmin || isHeadCoachUser || isCourtManagerUser) {
      setSelectedReservation(reservation);
      setIsReservationDialogOpen(true);
    }
  };

  const canEditReservation = (reservation: ICourtReservation) => {
    return reservation.status === ReservationStatus.Pending || isAdmin || isHeadCoachUser || isCourtManagerUser;
  };

  const handleDeleteReservation = (reservationId: string) => {
    setReservationToDelete(reservationId);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteReservation(reservationToDelete);
      dispatch(setGeneralAlertMessage({ message: "Reservation deleted successfully", type: "success" }));
      fetchReservations();
      setIsDeleteDialogOpen(false);
      setReservationToDelete("");
    } catch (error: any) {
      dispatch(setGeneralAlertMessage({ message: error?.message || "Failed to delete reservation", type: "error" }));
    }
  };

  const handleReservationDialogClose = (update?: boolean) => {
    setIsReservationDialogOpen(false);
    setSelectedReservation(undefined);
    setInitialReservationData(null);
    if (update) {
      fetchReservations();
    }
  };

  // After-midnight slots (hour < 7) represent times on the next day
  const getSlotMoment = (timeSlot: { hour: number; minute: number }) => {
    const base = timeSlot.hour < 7 ? currentDay.clone().add(1, "day") : currentDay.clone();
    return base.hour(timeSlot.hour).minute(timeSlot.minute).second(0).millisecond(0);
  };

  const getReservationsForSlot = (courtId: string, timeSlot: { hour: number; minute: number }) => {
    const dayReservations = reservationsByCourt[courtId] || [];

    return dayReservations.filter((reservation) => {
      const startLocal: moment.Moment = (reservation as any).__startLocal ?? moment.utc(reservation.startTime).local();

      const slotStart = getSlotMoment(timeSlot);
      const slotEnd = slotStart.clone().add(30, "minutes");

      return startLocal.isSameOrAfter(slotStart) && startLocal.isBefore(slotEnd);
    });
  };

  const getReservationStatusLabel = (status: ReservationStatus) => {
    switch (status) {
      case ReservationStatus.Pending:
        return "Pending";
      case ReservationStatus.Confirmed:
        return "Confirmed";
      case ReservationStatus.Cancelled:
        return "Cancelled";
      case ReservationStatus.Completed:
        return "Completed";
      default:
        return "Unknown";
    }
  };

  const getReportDateParams = () => {
    const now = moment();
    switch (reportFilterType) {
      case "thisMonth":
        return {
          startDate: now.clone().startOf("month").toISOString(),
          endDate: now.clone().endOf("month").toISOString(),
        };
      case "lastMonth":
        return {
          startDate: now.clone().subtract(1, "month").startOf("month").toISOString(),
          endDate: now.clone().subtract(1, "month").endOf("month").toISOString(),
        };
      case "custom":
        return {
          ...(reportStartDate ? { startDate: moment(reportStartDate).startOf("day").toISOString() } : {}),
          ...(reportEndDate ? { endDate: moment(reportEndDate).endOf("day").toISOString() } : {}),
        };
      default:
        return {
          startDate: moment().startOf("month").toISOString(),
          endDate: moment().endOf("month").toISOString(),
        };
    }
  };

  const [exportLoading, setExportLoading] = useState(false);

  const exportReportXlsx = async () => {
    if (!reportData) return;
    setExportLoading(true);
    try {
      const fmtDate = (d: string) => moment(d).format("YYYY-MM-DD");
      const rangeLabel = `${fmtDate(reportData.startDate)}_to_${fmtDate(reportData.endDate)}`;

      // Fetch every reservation in the date range, then bucket by category client-side
      // — one network round-trip vs one-per-category.
      const dateParams = getReportDateParams();
      const allRes = await getAllReservations(dateParams);
      const reservations = allRes.data || [];
      const byCategory = new Map<string, ICourtReservation[]>();
      for (const r of reservations) {
        const key = r.reservationCategoryId ?? "__uncategorized__";
        if (!byCategory.has(key)) byCategory.set(key, []);
        byCategory.get(key)!.push(r);
      }

      // Excel disallows : \ / ? * [ ] in sheet names and caps at 31 chars.
      const usedSheetNames = new Set<string>();
      const safeSheetName = (raw: string) => {
        let name = raw.replace(/[\\\/\?\*\[\]\:]/g, " ").trim() || "Sheet";
        if (name.length > 31) name = name.slice(0, 31);
        let candidate = name;
        let i = 2;
        while (usedSheetNames.has(candidate.toLowerCase())) {
          const suffix = ` (${i})`;
          candidate = (name.length + suffix.length > 31 ? name.slice(0, 31 - suffix.length) : name) + suffix;
          i++;
        }
        usedSheetNames.add(candidate.toLowerCase());
        return candidate;
      };

      const wb = XLSX.utils.book_new();

      // ── Summary sheet ──
      const summaryAoa: (string | number)[][] = [];
      summaryAoa.push(["Court Reservations Report"]);
      summaryAoa.push(["From", fmtDate(reportData.startDate), "To", fmtDate(reportData.endDate)]);
      summaryAoa.push(["Generated", moment().format("YYYY-MM-DD HH:mm")]);
      summaryAoa.push([]);
      summaryAoa.push(["Summary"]);
      summaryAoa.push(["Total Reservations", reportData.totalReservations]);
      summaryAoa.push(["Total Hours", Number(reportData.totalHours.toFixed(1))]);
      summaryAoa.push(["Total Revenue (EGP)", reportData.totalRevenue]);
      summaryAoa.push([]);
      summaryAoa.push(["Category Breakdown"]);
      summaryAoa.push([
        "Category",
        "Total",
        "Confirmed",
        "Pending",
        "Hours",
        "Confirmed Hours",
        "Pending Hours",
        "Revenue (EGP)",
      ]);
      for (const item of reportData.categoryBreakdown) {
        summaryAoa.push([
          item.categoryName || "Uncategorized",
          item.count,
          item.confirmedCount ?? "",
          item.pendingCount ?? "",
          Number(item.hours.toFixed(1)),
          item.confirmedHours != null ? Number(item.confirmedHours.toFixed(1)) : "",
          item.pendingHours != null ? Number(item.pendingHours.toFixed(1)) : "",
          item.confirmedRevenue ?? item.revenue,
        ]);
      }
      if (reportData.statusBreakdown && Object.keys(reportData.statusBreakdown).length > 0) {
        summaryAoa.push([]);
        summaryAoa.push(["Status Breakdown"]);
        summaryAoa.push(["Status", "Count"]);
        for (const [status, count] of Object.entries(reportData.statusBreakdown)) {
          summaryAoa.push([getStatusLabel(status), count]);
        }
      }
      const summarySheet = XLSX.utils.aoa_to_sheet(summaryAoa);
      summarySheet["!cols"] = [{ wch: 28 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 16 }, { wch: 14 }, { wch: 16 }];
      XLSX.utils.book_append_sheet(wb, summarySheet, safeSheetName("Summary"));

      // ── One sheet per category ──
      for (const item of reportData.categoryBreakdown) {
        const categoryLabel = item.categoryName || "Uncategorized";
        const key = item.reservationCategoryId ?? "__uncategorized__";
        const items = (byCategory.get(key) || []).slice().sort(
          (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
        );

        const aoa: (string | number)[][] = [];
        aoa.push([`Category: ${categoryLabel}`]);
        aoa.push([`From ${fmtDate(reportData.startDate)} to ${fmtDate(reportData.endDate)}`]);
        aoa.push([]);
        aoa.push(["Reservations", item.count]);
        aoa.push(["Hours", Number(item.hours.toFixed(1))]);
        if (item.confirmedHours != null) aoa.push(["Confirmed Hours", Number(item.confirmedHours.toFixed(1))]);
        if (item.pendingHours != null) aoa.push(["Pending Hours", Number(item.pendingHours.toFixed(1))]);
        aoa.push(["Revenue (EGP)", item.confirmedRevenue ?? item.revenue]);
        aoa.push([]);
        aoa.push(["Date", "Start", "End", "Hours", "Court", "User", "Status", "Tournament", "Notes", "Cost (EGP)"]);
        for (const r of items) {
          const start = moment(r.startTime);
          const end = moment(r.endTime);
          const hours = Math.max(0, end.diff(start, "minutes") / 60);
          const userLabel = r.isGuest ? `${r.guestName || "Guest"} (guest)` : r.userName;
          aoa.push([
            start.format("YYYY-MM-DD"),
            start.format("HH:mm"),
            end.format("HH:mm"),
            Number(hours.toFixed(2)),
            r.courtName,
            userLabel,
            getStatusLabel(String(r.status)),
            r.tournamentName ?? "",
            r.notes ?? "",
            r.cost,
          ]);
        }
        const sheet = XLSX.utils.aoa_to_sheet(aoa);
        sheet["!cols"] = [
          { wch: 12 }, { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 18 },
          { wch: 24 }, { wch: 12 }, { wch: 22 }, { wch: 28 }, { wch: 12 },
        ];
        XLSX.utils.book_append_sheet(wb, sheet, safeSheetName(categoryLabel));
      }

      XLSX.writeFile(wb, `court-reservations-report_${rangeLabel}.xlsx`);
    } catch (err: any) {
      dispatch(setGeneralAlertMessage({ message: err?.message || "Failed to export report", type: "error" }));
    } finally {
      setExportLoading(false);
    }
  };

  const fetchReport = async () => {
    setReportLoading(true);
    setSelectedReportCategory(null);
    setReportReservations([]);
    try {
      const params = getReportDateParams();
      const res = await getReservationReport(params);
      setReportData(res.data);
    } catch (error: any) {
      dispatch(setGeneralAlertMessage({ message: error?.message || "Failed to load report", type: "error" }));
    } finally {
      setReportLoading(false);
    }
  };

  const fetchReportReservations = async (categoryId: string | null) => {
    setReportReservationsLoading(true);
    try {
      const dateParams = getReportDateParams();
      const params: any = { ...dateParams };
      if (categoryId) {
        params.reservationCategoryId = categoryId;
      }
      const res = await getAllReservations(params);
      setReportReservations(res.data || []);
    } catch (error: any) {
      dispatch(setGeneralAlertMessage({ message: error?.message || "Failed to load reservations", type: "error" }));
    } finally {
      setReportReservationsLoading(false);
    }
  };

  const handleReportCategoryClick = (categoryId: string | null) => {
    if (selectedReportCategory === categoryId) {
      setSelectedReportCategory(null);
      setReportReservations([]);
    } else {
      setSelectedReportCategory(categoryId);
      fetchReportReservations(categoryId);
      // Scroll to the reservations section after a brief delay for render
      setTimeout(() => {
        reportReservationsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  };

  useEffect(() => {
    if (activeTab === "report") {
      fetchReport();
    }
    if (activeTab === "categories") {
      fetchAllCategories();
    }
  }, [activeTab, reportFilterType, reportStartDate, reportEndDate]);

  const fetchAllCategories = async () => {
    setCategoriesLoading(true);
    try {
      const res = await getAllCategories();
      setAllCategories(res.data);
    } catch (error: any) {
      dispatch(setGeneralAlertMessage({ message: error?.message || "Failed to load categories", type: "error" }));
    } finally {
      setCategoriesLoading(false);
    }
  };

  const handleCreateCategory = () => {
    setSelectedCategory(undefined);
    setIsCategoryDialogOpen(true);
  };

  const handleEditCategory = (category: IReservationCategory) => {
    setSelectedCategory(category);
    setIsCategoryDialogOpen(true);
  };

  const handleCategoryDialogClose = async (update?: boolean) => {
    setIsCategoryDialogOpen(false);
    setSelectedCategory(undefined);
    if (update) {
      fetchAllCategories();
      // Refresh active categories used by dialogs
      const categoriesRes = await getActiveCategories();
      setCategories(categoriesRes.data);
    }
  };

  const isSlotInCart = (courtId: string, timeSlot: { hour: number; minute: number }) => {
    const slotMoment = getSlotMoment(timeSlot);
    return cartItems.some((item) => {
      return item.courtId === courtId && moment(item.startTime).isSame(slotMoment);
    });
  };

  // Merge consecutive slots per court into single entries for display & submission
  const mergedCartItems = useMemo(() => {
    if (cartItems.length === 0) return [];

    // Group by courtId
    const byCourtMap: Record<string, CartItem[]> = {};
    cartItems.forEach((item) => {
      if (!byCourtMap[item.courtId]) byCourtMap[item.courtId] = [];
      byCourtMap[item.courtId].push(item);
    });

    const merged: { id: string; courtId: string; courtName: string; startTime: string; endTime: string; displayStart: string; displayEnd: string; slotIds: string[] }[] = [];

    Object.values(byCourtMap).forEach((items) => {
      // Sort by start time
      const sorted = [...items].sort((a, b) => moment(a.startTime).valueOf() - moment(b.startTime).valueOf());

      let currentGroup = {
        startTime: sorted[0].startTime,
        endTime: sorted[0].endTime,
        slotIds: [sorted[0].id],
      };

      for (let i = 1; i < sorted.length; i++) {
        const slotStart = moment(sorted[i].startTime);
        const groupEnd = moment(currentGroup.endTime);

        if (slotStart.isSame(groupEnd)) {
          // Consecutive - extend the group
          currentGroup.endTime = sorted[i].endTime;
          currentGroup.slotIds.push(sorted[i].id);
        } else {
          // Not consecutive - push current group and start new one
          merged.push({
            id: currentGroup.slotIds.join("_"),
            courtId: sorted[0].courtId,
            courtName: sorted[0].courtName,
            startTime: currentGroup.startTime,
            endTime: currentGroup.endTime,
            displayStart: moment(currentGroup.startTime).format("hh:mm A"),
            displayEnd: moment(currentGroup.endTime).format("hh:mm A"),
            slotIds: currentGroup.slotIds,
          });
          currentGroup = {
            startTime: sorted[i].startTime,
            endTime: sorted[i].endTime,
            slotIds: [sorted[i].id],
          };
        }
      }

      // Push last group
      merged.push({
        id: currentGroup.slotIds.join("_"),
        courtId: sorted[0].courtId,
        courtName: sorted[0].courtName,
        startTime: currentGroup.startTime,
        endTime: currentGroup.endTime,
        displayStart: moment(currentGroup.startTime).format("hh:mm A"),
        displayEnd: moment(currentGroup.endTime).format("hh:mm A"),
        slotIds: currentGroup.slotIds,
      });
    });

    return merged;
  }, [cartItems]);

  const cartTotal = useMemo(() => {
    if (mergedCartItems.length === 0) return 0;
    const selectedCategory = categories.find((c) => c.id === cartCategoryId);
    const pricePerHour = selectedCategory?.pricePerHour || 0;

    let total = 0;
    mergedCartItems.forEach((item) => {
      const durationHours = moment(item.endTime).diff(moment(item.startTime), "minutes") / 60;
      total += durationHours * pricePerHour;
      if (cartNeedBall[item.id]) total += 50;
    });
    return total;
  }, [mergedCartItems, cartCategoryId, cartNeedBall, categories]);

  const handleRemoveMergedItem = (slotIds: string[]) => {
    setCartItems((prev) => prev.filter((item) => !slotIds.includes(item.id)));
  };

  const handleClearCart = () => {
    setCartItems([]);
    setCartCategoryId("");
    setCartTournamentId("");
    setCartNeedBall({});
    setCartNotes("");
  };

  const handleCartSubmit = async () => {
    if (mergedCartItems.length === 0) return;

    setCartLoading(true);
    try {
      const bulkData: ICreateReservationRequest[] = mergedCartItems.map((item) => ({
        courtId: item.courtId,
        startTime: item.startTime,
        endTime: item.endTime,
        reservationCategoryId: cartCategoryId || undefined,
        tournamentId: isAdmin && cartTournamentId ? cartTournamentId : undefined,
        needBall: cartNeedBall[item.id] || undefined,
        notes: cartNotes || undefined,
        status: isAdmin ? ReservationStatus.Confirmed : undefined,
      }));

      await createBulkReservations({ reservations: bulkData });
      dispatch(
        setGeneralAlertMessage({
          message: `${mergedCartItems.length} reservation(s) created successfully`,
          type: "success",
        })
      );
      handleClearCart();
      setIsCartOpen(false);
      fetchReservations();
    } catch (error: any) {
      dispatch(setGeneralAlertMessage({ message: error?.message || "Failed to create reservations", type: "error" }));
    } finally {
      setCartLoading(false);
    }
  };

  // Clear cart when day changes
  useEffect(() => {
    setCartItems([]);
  }, [currentDay]);

  // If the selected tournament no longer matches the viewed day, clear it.
  useEffect(() => {
    if (!cartTournamentId) return;
    if (!cartAvailableTournaments.some((t) => t.id === cartTournamentId)) {
      setCartTournamentId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartAvailableTournaments]);

  const getStatusLabel = (status: string) => {
    const statusMap: Record<string, string> = {
      "1": "Pending",
      "2": "Confirmed",
      "3": "Cancelled",
      "4": "Completed",
      Pending: "Pending",
      Confirmed: "Confirmed",
      Cancelled: "Cancelled",
      Completed: "Completed",
    };
    return statusMap[status] || status;
  };

  const categoryChartData = useMemo(() => {
    if (!reportData?.categoryBreakdown) return [];
    const totalRevenue = reportData.totalRevenue || 1;
    return reportData.categoryBreakdown.map((item) => ({
      name: item.categoryName || "Uncategorized",
      reservationCategoryId: item.reservationCategoryId,
      count: item.count,
      hours: item.hours,
      revenue: item.confirmedRevenue ?? item.revenue,
      percentage: Math.round(((item.confirmedRevenue ?? item.revenue) / totalRevenue) * 100),
    }));
  }, [reportData]);

  // Sorted & filtered reservations for the list view
  const sortedDayReservations = useMemo(() => {
    return [...reservations]
      .filter((r) => r.status !== ReservationStatus.Cancelled)
      .filter((r) => !listFilterCourt || r.courtId === listFilterCourt)
      .filter((r) => {
        if (!listFilterCategory) return true;
        if (listFilterCategory === "uncategorized") return !r.reservationCategoryId;
        return r.reservationCategoryId === listFilterCategory;
      })
      .sort((a, b) => moment.utc(a.startTime).valueOf() - moment.utc(b.startTime).valueOf());
  }, [reservations, listFilterCourt, listFilterCategory]);

  // Group report reservations by user + time frame
  interface GroupedReservation {
    key: string;
    reservations: ICourtReservation[];
    courts: string[];
    userName: string;
    guestName: string | null;
    startTime: string;
    endTime: string;
    status: ReservationStatus;
    totalCost: number;
  }

  const groupedReportReservations = useMemo((): GroupedReservation[] => {
    if (reportReservations.length === 0) return [];

    const groups: Record<string, ICourtReservation[]> = {};
    reportReservations.forEach((r) => {
      // Group key: same user + same start/end time
      const key = `${r.userId}_${r.startTime}_${r.endTime}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(r);
    });

    return Object.entries(groups)
      .map(([key, reservations]) => ({
        key,
        reservations,
        courts: [...new Set(reservations.map((r) => r.courtName))].sort((a, b) => a.localeCompare(b)),
        userName: reservations[0].userName,
        guestName: reservations[0].guestName || null,
        startTime: reservations[0].startTime,
        endTime: reservations[0].endTime,
        status: reservations[0].status,
        totalCost: reservations.reduce((sum, r) => sum + r.cost, 0),
      }))
      .sort((a, b) => moment.utc(b.startTime).valueOf() - moment.utc(a.startTime).valueOf());
  }, [reportReservations]);

  if (loading && courts.length === 0) {
    return (
      <div className="court-reservations-page">
        <Loader minHeight={400} />
      </div>
    );
  }

  const canDeleteReservation = (reservation: ICourtReservation) =>
    reservation.status === ReservationStatus.Pending || isAdmin || isHeadCoachUser || isCourtManagerUser;

  const tabOptions: TabOption<ReservationsTab>[] = [
    { value: "calendar", label: "Calendar", icon: <Calendar size={16} /> },
    { value: "report", label: "Report", icon: <BarChart3 size={16} /> },
    { value: "categories", label: "Categories", icon: <Settings size={16} /> },
    ...(canViewCourtsTab ? [{ value: "courts" as const, label: "Courts", icon: <LayoutGrid size={16} /> }] : []),
  ];

  const headerActions = canCreateRegular ? (
    <div className="crv-split">
      <Button icon={<Plus size={18} />} onClick={handleCreateReservation}>
        Create Reservation
      </Button>
      <Menu
        label="More options"
        items={[
          {
            label: "Add Recurring Training",
            icon: <Calendar size={16} />,
            onClick: handleCreateRecurringTraining,
            visible: canManageTraining,
          },
          { label: "Add Tournament Reservation", icon: <Plus size={16} />, onClick: handleCreateBulkReservations },
        ]}
        trigger={({ ref, ...triggerProps }) => (
          <Button
            {...triggerProps}
            ref={ref}
            className="crv-split__toggle"
            aria-label="More options"
            data-tooltip="More options"
            icon={<ChevronDown size={18} />}
          />
        )}
      />
    </div>
  ) : (
    <Button icon={<Calendar size={18} />} onClick={handleCreateRecurringTraining}>
      Add Recurring Training
    </Button>
  );

  return (
    <div className="court-reservations-page">
      <PageHeader title="Court Reservations" subtitle="Manage court bookings and reservations" actions={headerActions}>
        {canViewReport && (
          <Tabs ariaLabel="Court reservations sections" options={tabOptions} value={activeTab} onChange={setActiveTab} />
        )}
      </PageHeader>

      {activeTab === "calendar" ? (
        <>
          <div className="crv-daybar">
            <IconButton label="Previous day" icon={<ChevronLeft size={18} />} onClick={handlePreviousDay} />
            <Button variant="ghost" size="sm" icon={<Calendar size={16} />} onClick={handleToday}>
              Today
            </Button>
            <div className="crv-daybar__date">{currentDay.format("dddd, MMMM DD, YYYY")}</div>
            <IconButton label="Next day" icon={<ChevronRight size={18} />} onClick={handleNextDay} />
            <Segmented
              ariaLabel="View"
              className="crv-view-toggle"
              value={calendarViewMode}
              onChange={setCalendarViewMode}
              options={[
                {
                  value: "calendar",
                  label: (
                    <span className="crv-view-toggle__item" data-tooltip="Calendar View">
                      <Calendar size={16} aria-hidden="true" />
                      <span className="ui-visually-hidden">Calendar View</span>
                    </span>
                  ),
                },
                {
                  value: "list",
                  label: (
                    <span className="crv-view-toggle__item" data-tooltip="List View">
                      <List size={16} aria-hidden="true" />
                      <span className="ui-visually-hidden">List View</span>
                    </span>
                  ),
                },
              ]}
            />
          </div>

          {calendarViewMode === "calendar" && (
            <div className="crv-grid-wrap">
              {loading && (
                <div className="crv-grid__loading">
                  <Spinner size="lg" />
                </div>
              )}
              <div
                className="crv-grid"
                style={{ "--crv-courts": Math.max(1, sortedCourts.length) } as React.CSSProperties}
              >
                <div className="crv-grid__head">
                  <div className="crv-grid__corner" />
                  {sortedCourts.map((court) => (
                    <div key={court.id} className="crv-grid__court">
                      <div className="crv-grid__court-name">{court.name}</div>
                      {!court.isActive && <div className="crv-grid__court-status">(Inactive)</div>}
                    </div>
                  ))}
                </div>

                <div className="crv-grid__body">
                  <div className="crv-grid__times">
                    {timeSlots.map((slot, index) => {
                      const label = moment().hour(slot.hour).minute(slot.minute).format("hh:mm A");
                      const now = moment();
                      const slotStart = getSlotMoment(slot);
                      const slotEnd = slotStart.clone().add(30, "minutes");
                      const isSlotToday = slotStart.isSame(now, "day");
                      const isCurrentTime = isSlotToday && now.isSameOrAfter(slotStart) && now.isBefore(slotEnd);
                      return (
                        <div key={index} className={`crv-grid__time${isCurrentTime ? " is-now" : ""}`}>
                          {label}
                        </div>
                      );
                    })}
                  </div>

                  <div className="crv-grid__columns">
                    {sortedCourts.map((court) => (
                      <div key={court.id} className="crv-grid__column">
                        {timeSlots.map((timeSlot, slotIndex) => {
                          const now = moment();
                          const slotStart = getSlotMoment(timeSlot);
                          const slotEnd = slotStart.clone().add(30, "minutes");
                          const isSlotToday = slotStart.isSame(now, "day");
                          const isCurrentTimeSlot = isSlotToday && now.isSameOrAfter(slotStart) && now.isBefore(slotEnd);

                          const slotReservations = getReservationsForSlot(court.id, timeSlot);

                          const hasReservations = slotReservations.length > 0;
                          const inCart = isSlotInCart(court.id, timeSlot);
                          const canClickSlot = canCreateRegular && !hasReservations && court.isActive;

                          return (
                            <div
                              key={slotIndex}
                              className={[
                                "crv-slot",
                                isCurrentTimeSlot ? "is-now" : "",
                                canClickSlot ? "is-open" : "",
                                inCart ? "is-in-cart" : "",
                              ]
                                .filter(Boolean)
                                .join(" ")}
                              onClick={() => {
                                if (canClickSlot) {
                                  handleSlotClick(court.id, timeSlot);
                                }
                              }}
                            >
                              {slotReservations.map((reservation, resIndex) => {
                                const startLocal: moment.Moment =
                                  (reservation as any).__startLocal ?? moment.utc(reservation.startTime).local();
                                const endLocal: moment.Moment =
                                  (reservation as any).__endLocal ?? moment.utc(reservation.endTime).local();

                                const minutesFromSlotStart = Math.max(0, startLocal.diff(slotStart, "minutes", true));
                                const duration = endLocal.diff(startLocal, "minutes", true);

                                const heightInSlots = Math.max(0.5, duration / 30);
                                const height = heightInSlots * 100;
                                const offset = Math.max(0, Math.min(100, (minutesFromSlotStart / 30) * 100));

                                // Check if reservation belongs to head coach user
                                const isReservationByHeadCoach = isHeadCoachUser && reservation.userId === currentUser?.id;
                                const shouldGrayOut = isHeadCoachUser && !isReservationByHeadCoach;
                                const isEditable = canEditReservation(reservation) && !shouldGrayOut;

                                const categoryName = reservation.reservationCategoryName;
                                const showCategoryBadge = categoryName && categoryName !== "User";

                                return (
                                  <div
                                    key={reservation.id}
                                    className={[
                                      "crv-block",
                                      `crv-status--${statusKeyOf(reservation.status)}`,
                                      isEditable ? "is-editable" : "",
                                      shouldGrayOut ? "is-muted" : "",
                                    ]
                                      .filter(Boolean)
                                      .join(" ")}
                                    style={{
                                      height: `${height}%`,
                                      top: `${offset}%`,
                                      zIndex: 1 + resIndex,
                                    }}
                                    onClick={() => {
                                      if (isEditable) {
                                        handleEditReservation(reservation);
                                      }
                                    }}
                                    data-tooltip={
                                      shouldGrayOut
                                        ? undefined
                                        : reservation.guestName
                                          ? `${reservation.courtName} - Guest: ${reservation.guestName || reservation.userName
                                          }${reservation.guestEmail ? ` (${reservation.guestEmail})` : ""
                                          } (${getReservationStatusLabel(reservation.status)})${showCategoryBadge
                                            ? ` [${categoryName}]` : ""}${reservation.cost > 0 ? ` - EGP ${reservation.cost}` : ""}`
                                          : `${reservation.courtName} - ${reservation.userName} (${getReservationStatusLabel(
                                            reservation.status
                                          )})${showCategoryBadge
                                            ? ` [${categoryName}]` : ""}${reservation.cost > 0 ? ` - EGP ${reservation.cost}` : ""}`
                                    }
                                  >
                                    <div className="crv-block__content">
                                      <div className="crv-block__court">{reservation.courtName}</div>
                                      <div className="crv-block__time">
                                        {startLocal.format("hh:mm A")} - {endLocal.format("hh:mm A")}
                                      </div>
                                      {reservation.guestName ? (
                                        <>
                                          <div className="crv-block__user">
                                            <span className="crv-block__guest-label">Guest: </span>
                                            {reservation.guestName || reservation.userName}
                                          </div>
                                          {reservation.guestEmail && (
                                            <div className="crv-block__guest-info">{reservation.guestEmail}</div>
                                          )}
                                          {reservation.guestPhone && (
                                            <div className="crv-block__guest-info">
                                              <a
                                                href={`https://wa.me/${reservation.guestPhone
                                                  .replace(/[^0-9+]/g, "")
                                                  .replace(/^\+/, "")}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(e) => e.stopPropagation()}
                                              >
                                                {reservation.guestPhone}
                                              </a>
                                            </div>
                                          )}
                                        </>
                                      ) : (
                                        <div className="crv-block__user">{reservation.userName}</div>
                                      )}
                                      {showCategoryBadge && (
                                        <Tag tone={categoryToneOf(categoryName)} className="crv-block__tag">
                                          {categoryName}
                                        </Tag>
                                      )}
                                      {reservation.tournamentName && (
                                        <div className="crv-block__tournament" data-tooltip={reservation.tournamentName}>
                                          <TagIcon size={10} />
                                          <span>{reservation.tournamentName}</span>
                                        </div>
                                      )}
                                      {reservation.cost > 0 && <div className="crv-block__cost">EGP {reservation.cost}</div>}
                                      {reservation.notes && <div className="crv-block__notes">{reservation.notes}</div>}
                                      {canDeleteReservation(reservation) && (
                                        <IconButton
                                          variant="danger"
                                          size="sm"
                                          className="crv-block__delete"
                                          label="Delete reservation"
                                          icon={<X size={14} />}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteReservation(reservation.id);
                                          }}
                                        />
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {calendarViewMode === "list" && (
            <Card
              className="crv-day-list"
              padding="none"
              title={`Reservations for ${currentDay.format("MMMM DD, YYYY")}`}
              actions={
                <span className="crv-count">
                  {sortedDayReservations.length} reservation{sortedDayReservations.length !== 1 ? "s" : ""}
                </span>
              }
            >
              <div className="crv-day-list__filters">
                <Field label="Court" className="crv-filter">
                  {(id) => (
                    <Select id={id} size="sm" block value={listFilterCourt} onChange={(e) => setListFilterCourt(e.target.value)}>
                      <option value="">All Courts</option>
                      {sortedCourts.map((court) => (
                        <option key={court.id} value={court.id}>
                          {court.name}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
                <Field label="Category" className="crv-filter">
                  {(id) => (
                    <Select id={id} size="sm" block value={listFilterCategory} onChange={(e) => setListFilterCategory(e.target.value)}>
                      <option value="">All Categories</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                      <option value="uncategorized">Uncategorized</option>
                    </Select>
                  )}
                </Field>
              </div>
              {loading ? (
                <Loader minHeight={200} />
              ) : sortedDayReservations.length === 0 ? (
                <EmptyState compact icon={<Calendar />} title="No reservations for this day." />
              ) : (
                <div className="crv-rows crv-rows--day">
                  {sortedDayReservations.map((reservation) => {
                    const startLocal = (reservation as any).__startLocal ?? moment.utc(reservation.startTime).local();
                    const endLocal = (reservation as any).__endLocal ?? moment.utc(reservation.endTime).local();
                    const categoryName = reservation.reservationCategoryName;
                    const showCategory = categoryName && categoryName !== "User";
                    const statusKey = statusKeyOf(reservation.status);
                    return (
                      <div
                        key={reservation.id}
                        className={`crv-row crv-status--${statusKey}${canEditReservation(reservation) ? " is-clickable" : ""}`}
                        onClick={() => canEditReservation(reservation) && handleEditReservation(reservation)}
                      >
                        <span className="crv-row__bar" aria-hidden="true" />
                        <div className="crv-row__main">
                          <div className="crv-row__top">
                            <span className="crv-row__time">
                              <Clock size={14} />
                              {startLocal.format("hh:mm A")} - {endLocal.format("hh:mm A")}
                            </span>
                            <span className="crv-row__meta">
                              <MapPin size={14} />
                              {reservation.courtName}
                            </span>
                          </div>
                          <div className="crv-row__middle">
                            <span className="crv-row__user">
                              <User size={14} />
                              {reservation.guestName ? (
                                <span>Guest: {reservation.guestName}</span>
                              ) : (
                                <span>{reservation.userName}</span>
                              )}
                            </span>
                            {showCategory && <Tag tone={categoryToneOf(categoryName)}>{categoryName}</Tag>}
                            {reservation.tournamentName && (
                              <Tag
                                tone="info"
                                icon={<TagIcon size={11} />}
                                className="crv-row__tournament"
                                data-tooltip={reservation.tournamentName}
                              >
                                {reservation.tournamentName}
                              </Tag>
                            )}
                          </div>
                          <div className="crv-row__bottom">
                            <Tag tone={STATUS_TAG_TONES[statusKey]}>{getReservationStatusLabel(reservation.status)}</Tag>
                            {reservation.cost > 0 && (
                              <span className="crv-row__cost">EGP {reservation.cost.toLocaleString()}</span>
                            )}
                            {reservation.needBall && <Tag tone="neutral">Ball</Tag>}
                          </div>
                          {reservation.notes && <div className="crv-row__notes">{reservation.notes}</div>}
                        </div>
                        {canDeleteReservation(reservation) && (
                          <IconButton
                            variant="danger"
                            className="crv-row__delete"
                            label="Delete reservation"
                            icon={<X size={16} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteReservation(reservation.id);
                            }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          )}

          {calendarViewMode === "calendar" && (
            <div className="crv-legend">
              {LEGEND_STATUSES.map((status) => (
                <div key={status} className="crv-legend__item">
                  <span className={`crv-legend__swatch crv-status--${statusKeyOf(status)}`} aria-hidden="true" />
                  <span>{getReservationStatusLabel(status)}</span>
                </div>
              ))}
            </div>
          )}
        </>
      ) : activeTab === "report" ? (
        <div className="crv-report">
          <Toolbar
            className="crv-report__controls"
            actions={
              <Button
                variant="tint"
                icon={<Download size={16} />}
                onClick={exportReportXlsx}
                disabled={!reportData || reportLoading || exportLoading}
                data-tooltip="Export the current report as Excel (one sheet per category)"
              >
                {exportLoading ? "Exporting…" : "Export"}
              </Button>
            }
          >
            <Select
              className="crv-report__period"
              aria-label="Report period"
              value={reportFilterType}
              onChange={(e) => {
                const val = e.target.value as any;
                setReportFilterType(val);
                if (val !== "custom") {
                  setReportStartDate("");
                  setReportEndDate("");
                }
              }}
            >
              <option value="thisMonth">This Month</option>
              <option value="lastMonth">Last Month</option>
              <option value="custom">Custom Range</option>
            </Select>
            {reportFilterType === "custom" && (
              <div className="crv-report__range">
                <div className="crv-report__date">
                  <label htmlFor="crv-report-from">From</label>
                  <DatePicker
                    id="crv-report-from"
                    block={false}
                    selected={reportStartDate ? new Date(reportStartDate) : null}
                    onChange={(date: Date | null) => setReportStartDate(date ? moment(date).format("YYYY-MM-DD") : "")}
                    dateFormat="MMM dd, yyyy"
                    placeholderText="Start date"
                    maxDate={reportEndDate ? new Date(reportEndDate) : undefined}
                    isClearable
                  />
                </div>
                <div className="crv-report__date">
                  <label htmlFor="crv-report-to">To</label>
                  <DatePicker
                    id="crv-report-to"
                    block={false}
                    selected={reportEndDate ? new Date(reportEndDate) : null}
                    onChange={(date: Date | null) => setReportEndDate(date ? moment(date).format("YYYY-MM-DD") : "")}
                    dateFormat="MMM dd, yyyy"
                    placeholderText="End date"
                    minDate={reportStartDate ? new Date(reportStartDate) : undefined}
                    isClearable
                  />
                </div>
              </div>
            )}
          </Toolbar>

          {reportLoading ? (
            <Loader minHeight={300} />
          ) : reportData ? (
            <>
              {/* Donut chart card */}
              <Card className="crv-donut">
                <div className="crv-donut__inner">
                  <div className="crv-donut__chart">
                    <ResponsiveContainer width={220} height={220}>
                      <PieChart>
                        <Pie
                          data={categoryChartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={95}
                          paddingAngle={2}
                          dataKey="revenue"
                          stroke="none"
                        >
                          {categoryChartData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={CATEGORY_CHART_COLORS[index % CATEGORY_CHART_COLORS.length]}
                              cursor="pointer"
                              onClick={() => handleReportCategoryClick(entry.reservationCategoryId)}
                            />
                          ))}
                        </Pie>
                        <RechartsTooltip
                          formatter={(value: number, name: string) => [`EGP ${value.toLocaleString()}`, name]}
                          contentStyle={{
                            border: "1px solid var(--line)",
                            borderRadius: 4,
                            boxShadow: "var(--shadow-pop)",
                            fontFamily: "var(--font-body)",
                            fontSize: 13,
                          }}
                          itemStyle={{ color: "var(--ink)" }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="crv-donut__summary">
                    <div className="crv-donut__label">TOTAL</div>
                    <div className="crv-donut__value">{reportData.totalRevenue.toLocaleString()} EGP</div>
                    <div className="crv-donut__count">
                      {reportData.totalReservations} reservation{reportData.totalReservations !== 1 ? "s" : ""}
                    </div>
                    <div className="crv-donut__legend">
                      {categoryChartData.map((item, index) => (
                        <button
                          type="button"
                          key={item.reservationCategoryId || `cat-${index}`}
                          className={`crv-donut__legend-item${selectedReportCategory === item.reservationCategoryId ? " is-selected" : ""}`}
                          aria-pressed={selectedReportCategory === item.reservationCategoryId}
                          onClick={() => handleReportCategoryClick(item.reservationCategoryId)}
                        >
                          <span
                            className="crv-donut__swatch"
                            style={{ backgroundColor: CATEGORY_CHART_COLORS[index % CATEGORY_CHART_COLORS.length] }}
                          />
                          <span className="crv-donut__legend-name">{item.name}</span>
                          <span className="crv-donut__legend-value">{item.revenue.toLocaleString()} EGP</span>
                          <span className="crv-donut__legend-pct">{item.percentage}%</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </Card>

              {/* Category breakdown table */}
              <TableWrap className="crv-breakdown">
                <table className="ui-table">
                  <thead>
                    <tr>
                      <th className="crv-sticky-left">Category</th>
                      <th className="ui-table__num">Total</th>
                      <th className="ui-table__num">Confirmed</th>
                      <th className="ui-table__num">Pending</th>
                      <th className="ui-table__num">Hours</th>
                      <th className="ui-table__num">Confirmed Hours</th>
                      <th className="ui-table__num">Pending Hours</th>
                      <th className="ui-table__num crv-sticky-right">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.categoryBreakdown.map((item, index) => (
                      <tr
                        key={item.reservationCategoryId || `uncategorized-${index}`}
                        className={`is-clickable${selectedReportCategory === item.reservationCategoryId ? " is-selected" : ""}`}
                        onClick={() => handleReportCategoryClick(item.reservationCategoryId)}
                      >
                        <td className="crv-sticky-left">
                          <div className="crv-breakdown__category">
                            <span
                              className="crv-breakdown__dot"
                              style={{ backgroundColor: CATEGORY_CHART_COLORS[index % CATEGORY_CHART_COLORS.length] }}
                            />
                            {item.categoryName || "Uncategorized"}
                          </div>
                        </td>
                        <td className="ui-table__num">{item.count}</td>
                        <td className="ui-table__num">{item.confirmedCount ?? "-"}</td>
                        <td className="ui-table__num">{item.pendingCount ?? "-"}</td>
                        <td className="ui-table__num">{item.hours.toFixed(1)}</td>
                        <td className="ui-table__num">{item.confirmedHours != null ? item.confirmedHours.toFixed(1) : "-"}</td>
                        <td className="ui-table__num">{item.pendingHours != null ? item.pendingHours.toFixed(1) : "-"}</td>
                        <td className="ui-table__num crv-sticky-right">
                          EGP {(item.confirmedRevenue ?? item.revenue).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td className="crv-sticky-left">TOTAL</td>
                      <td className="ui-table__num">{reportData.totalReservations}</td>
                      <td colSpan={5}></td>
                      <td className="ui-table__num crv-sticky-right">EGP {reportData.totalRevenue.toLocaleString()}</td>
                    </tr>
                  </tfoot>
                </table>
              </TableWrap>

              {/* Status breakdown */}
              {reportData.statusBreakdown && Object.keys(reportData.statusBreakdown).length > 0 && (
                <Card title="Status Breakdown">
                  <div className="crv-status-cards">
                    {Object.entries(reportData.statusBreakdown).map(([status, count]) => {
                      const statusKey = statusKeyOf(status);
                      return (
                        <StatCard
                          key={status}
                          label={getStatusLabel(status)}
                          value={count}
                          tone={STATUS_STAT_TONES[statusKey]}
                          icon={STATUS_ICONS[statusKey]}
                        />
                      );
                    })}
                  </div>
                </Card>
              )}

              {/* Filtered reservations list when a category is selected */}
              {selectedReportCategory !== null && (
                <div ref={reportReservationsRef} className="crv-report__anchor">
                  <Card
                    padding="none"
                    title={`Reservations: ${categoryChartData.find((c) => c.reservationCategoryId === selectedReportCategory)?.name || "Uncategorized"}`}
                    actions={
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<X size={16} />}
                        onClick={() => {
                          setSelectedReportCategory(null);
                          setReportReservations([]);
                        }}
                      >
                        Clear Filter
                      </Button>
                    }
                  >
                    {reportReservationsLoading ? (
                      <Loader minHeight={100} />
                    ) : groupedReportReservations.length === 0 ? (
                      <EmptyState compact title="No reservations found." />
                    ) : (
                      <div className="crv-rows crv-rows--report">
                        {groupedReportReservations.map((group) => {
                          const startLocal = moment.utc(group.startTime).local();
                          const endLocal = moment.utc(group.endTime).local();
                          const firstRes = group.reservations[0];
                          const isClickable = group.reservations.length === 1 && canEditReservation(firstRes);
                          const statusKey = statusKeyOf(group.status);
                          return (
                            <div
                              key={group.key}
                              className={`crv-row crv-status--${statusKey}${isClickable ? " is-clickable" : ""}`}
                              onClick={() => isClickable && handleEditReservation(firstRes)}
                            >
                              <span className="crv-row__bar" aria-hidden="true" />
                              <div className="crv-row__main">
                                <div className="crv-row__top">
                                  <span className="crv-row__date">{startLocal.format("MMM DD, YYYY")}</span>
                                  <span className="crv-row__plain">
                                    {startLocal.format("hh:mm A")} - {endLocal.format("hh:mm A")}
                                  </span>
                                  <span className="crv-row__meta">
                                    {group.courts.length > 1
                                      ? `${group.courts.join(", ")} (${group.courts.length} courts)`
                                      : group.courts[0]}
                                  </span>
                                </div>
                                <div className="crv-row__bottom">
                                  <span className="crv-row__plain">
                                    {group.guestName ? `Guest: ${group.guestName}` : group.userName}
                                  </span>
                                  <Tag tone={STATUS_TAG_TONES[statusKey]}>{getReservationStatusLabel(group.status)}</Tag>
                                  {group.totalCost > 0 && (
                                    <span className="crv-row__cost crv-row__cost--inline">
                                      EGP {group.totalCost.toLocaleString()}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </Card>
                </div>
              )}
            </>
          ) : (
            <Card>
              <EmptyState compact icon={<BarChart3 />} title="No report data available for the selected period." />
            </Card>
          )}
        </div>
      ) : activeTab === "categories" ? (
        <Card
          className="crv-categories"
          padding="none"
          title="Reservation Categories"
          actions={
            canManageCategories && (
              <Button size="sm" icon={<Plus size={16} />} onClick={handleCreateCategory}>
                Add Category
              </Button>
            )
          }
        >
          {categoriesLoading ? (
            <Loader minHeight={200} />
          ) : allCategories.length === 0 ? (
            <EmptyState compact icon={<Settings />} title="No categories found. Create one to get started." />
          ) : (
            <>
              {/* Desktop table view */}
              <div className="crv-categories__table">
                <table className="ui-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th className="ui-table__num">Price/Hour</th>
                      <th>Status</th>
                      <th className="ui-table__num">Reservations</th>
                      {canManageCategories && <th className="ui-table__actions">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {allCategories.map((category) => (
                      <tr key={category.id}>
                        <td className="ui-table__primary">{category.name}</td>
                        <td className="ui-table__num">EGP {category.pricePerHour.toLocaleString()}</td>
                        <td>
                          <Tag tone={category.isActive ? "ok" : "neutral"}>{category.isActive ? "Active" : "Inactive"}</Tag>
                        </td>
                        <td className="ui-table__num">{category.reservationCount}</td>
                        {canManageCategories && (
                          <td className="ui-table__actions">
                            <IconButton label="Edit" icon={<Pencil size={14} />} onClick={() => handleEditCategory(category)} />
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile card view */}
              <div className="crv-categories__cards">
                {allCategories.map((category) => (
                  <div key={category.id} className="crv-category-card">
                    <div className="crv-category-card__head">
                      <div className="crv-category-card__name">{category.name}</div>
                      <Tag tone={category.isActive ? "ok" : "neutral"}>{category.isActive ? "Active" : "Inactive"}</Tag>
                    </div>
                    <div className="crv-category-card__details">
                      <div className="crv-category-card__detail">
                        <span className="crv-category-card__label">Price/Hour</span>
                        <span className="crv-category-card__value">EGP {category.pricePerHour.toLocaleString()}</span>
                      </div>
                      <div className="crv-category-card__detail">
                        <span className="crv-category-card__label">Reservations</span>
                        <span className="crv-category-card__value">{category.reservationCount}</span>
                      </div>
                    </div>
                    {canManageCategories && (
                      <div className="crv-category-card__actions">
                        <Button variant="tint" size="sm" icon={<Pencil size={14} />} onClick={() => handleEditCategory(category)}>
                          Edit
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>
      ) : activeTab === "courts" ? (
        <CourtsManagement canManage={canViewCourtsTab} />
      ) : null}

      {/* Cart floating button */}
      {activeTab === "calendar" && cartItems.length > 0 && !isCartOpen && (
        <button type="button" className="crv-cart-fab" aria-label="Cart" data-tooltip="Cart" onClick={() => setIsCartOpen(true)}>
          <ShoppingCart size={20} />
          <span className="crv-cart-fab__count">{mergedCartItems.length}</span>
        </button>
      )}

      {/* Cart panel */}
      <Dialog
        open={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        placement="side"
        size="sm"
        icon={<ShoppingCart size={18} />}
        title={`Cart (${mergedCartItems.length})`}
        busy={cartLoading}
        bodyClassName="crv-cart"
        footer={
          mergedCartItems.length > 0 ? (
            <div className="crv-cart__foot">
              {cartTotal > 0 && (
                <div className="crv-cart__total">
                  <span>Total</span>
                  <span>EGP {cartTotal.toFixed(0)}</span>
                </div>
              )}
              <div className="crv-cart__actions">
                <Button variant="ghost" onClick={handleClearCart} disabled={cartLoading}>
                  Clear All
                </Button>
                <Button className="crv-cart__submit" onClick={handleCartSubmit} loading={cartLoading}>
                  {`Create ${mergedCartItems.length} Reservation${mergedCartItems.length !== 1 ? "s" : ""}`}
                </Button>
              </div>
            </div>
          ) : undefined
        }
      >
        {mergedCartItems.length === 0 ? (
          <EmptyState compact icon={<ShoppingCart />} title="Click on empty time slots to add reservations" />
        ) : (
          <>
            <div className="crv-cart__items">
              {mergedCartItems.map((item) => (
                <div key={item.id} className="crv-cart__item">
                  <div className="crv-cart__info">
                    <div className="crv-cart__court">{item.courtName}</div>
                    <div className="crv-cart__time">
                      {item.displayStart} - {item.displayEnd}
                    </div>
                    <Checkbox
                      className="crv-cart__ball"
                      checked={!!cartNeedBall[item.id]}
                      onChange={(checked) => setCartNeedBall((prev) => ({ ...prev, [item.id]: checked }))}
                      disabled={cartLoading}
                      label="Ball (+50 EGP)"
                    />
                  </div>
                  <IconButton
                    variant="danger"
                    size="sm"
                    label="Remove"
                    icon={<X size={14} />}
                    onClick={() => handleRemoveMergedItem(item.slotIds)}
                    disabled={cartLoading}
                  />
                </div>
              ))}
            </div>

            <div className="crv-cart__options">
              <Field label="Category">
                {(id) => (
                  <Select
                    id={id}
                    block
                    value={cartCategoryId}
                    onChange={(e) => setCartCategoryId(e.target.value)}
                    disabled={cartLoading}
                  >
                    <option value="">No category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name} (EGP {cat.pricePerHour}/hr)
                      </option>
                    ))}
                  </Select>
                )}
              </Field>

              {isAdmin && tournaments.length > 0 && (
                <Field
                  label={
                    <>
                      Tournament <span className="crv-optional">(optional)</span>
                    </>
                  }
                >
                  {(id) => (
                    <Select
                      id={id}
                      block
                      value={cartTournamentId}
                      onChange={(e) => setCartTournamentId(e.target.value)}
                      disabled={cartLoading || cartAvailableTournaments.length === 0}
                    >
                      <option value="">
                        {cartAvailableTournaments.length === 0 ? "No tournaments on this date" : "No tournament"}
                      </option>
                      {cartAvailableTournaments.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name} · {moment(t.startDate).format("DD MMM")} – {moment(t.endDate).format("DD MMM YYYY")}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
              )}

              <Field label="Notes">
                {(id) => (
                  <Textarea
                    id={id}
                    placeholder="Optional notes for all reservations"
                    value={cartNotes}
                    onChange={(e) => setCartNotes(e.target.value)}
                    rows={2}
                    disabled={cartLoading}
                  />
                )}
              </Field>
            </div>
          </>
        )}
      </Dialog>

      {isReservationDialogOpen && (
        <ReservationDialog
          reservation={selectedReservation}
          courts={courts}
          categories={categories}
          tournaments={tournaments}
          isOpen={isReservationDialogOpen}
          onClose={handleReservationDialogClose}
          initialCourtId={initialReservationData?.courtId}
          initialDate={initialReservationData?.date}
          initialStartTime={initialReservationData?.startTime}
        />
      )}

      {isRecurringTrainingDialogOpen && (
        <RecurringTrainingDialog
          courts={courts}
          categories={categories}
          isOpen={isRecurringTrainingDialogOpen}
          onClose={(update) => {
            setIsRecurringTrainingDialogOpen(false);
            if (update) {
              fetchReservations();
            }
          }}
        />
      )}

      {isBulkReservationDialogOpen && (
        <BulkReservationDialog
          courts={courts}
          categories={categories}
          tournaments={tournaments}
          isOpen={isBulkReservationDialogOpen}
          initialCategoryId={eventCategory?.id}
          onClose={(update) => {
            setIsBulkReservationDialogOpen(false);
            if (update) {
              fetchReservations();
            }
          }}
        />
      )}

      {isCategoryDialogOpen && (
        <CategoryDialog
          category={selectedCategory}
          isOpen={isCategoryDialogOpen}
          onClose={handleCategoryDialogClose}
        />
      )}

      {isDeleteDialogOpen && (
        <DeleteReservationDialog
          reservationId={reservationToDelete}
          isOpen={isDeleteDialogOpen}
          onDelete={handleDeleteConfirm}
          onClose={() => {
            setIsDeleteDialogOpen(false);
            setReservationToDelete("");
          }}
        />
      )}
    </div>
  );
};

export default CourtReservations;
